import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  TextField,
} from '@mui/material';

import { supportedLanguages } from '@/translations/config';
import OrganizationsDropdown from '@/components/OrganizationsDropdown/OrganizationsDropdown';
import RolesDropdown from '@/components/RolesDropdown/RolesDropdown';
import FacilityUserStatusChip from '@/components/FacilityUserStatusChip/FacilityUserStatusChip';
import { USER_STATUS } from './userHistory.utils';
import FacilityAccessComponent from './FacilityAccessComponent';

const UserBasicInfoSection = forwardRef(
  (
    {
      user = null,
      onFormChange = () => {},
      selectedFacilities = [],
      onSelectedFacilitiesChange = () => {},
      onResendActivation = () => {},
      permissionsType = 'permission',
      onPermissionsTypeChange = () => {},
      selectedRoleId = null,
      onRoleChange = () => {},
      selectedOrganization = '',
      onOrganizationChange = () => {},
      hasAllFacilitiesAccess = false,
      onHasAllFacilitiesAccessChange = () => {},
      roleBasedFacilitiesAccess = false,
      setRoleBasedFacilitiesAccess = () => {},
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const [basicInfo, setBasicInfo] = useState({
      email: '',
      userName: '',
      mfa_type: 'NONE',
      languagePreference: 'en',
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
      if (user) {
        setBasicInfo({
          email: user.user_data?.email || '',
          userName: user.user_data?.name || '',
          mfa_type: user.user_data?.mfa_type || 'NONE',
          languagePreference: user.user_data?.language_preference || 'en',
        });
      } else {
        setBasicInfo({
          email: '',
          userName: '',
          mfa_type: 'NONE',
          languagePreference: 'en',
        });
      }
      setErrors({});
    }, [user]);

    useEffect(() => {
      onFormChange(basicInfo);
    }, [basicInfo, onFormChange]);

    useEffect(() => {
      if (
        (selectedFacilities.length > 0 || hasAllFacilitiesAccess) &&
        errors.facilities
      ) {
        setErrors((prev) => ({ ...prev, facilities: null }));
      }
    }, [selectedFacilities, hasAllFacilitiesAccess]);

    const handleFormChange = (field, value) => {
      setBasicInfo((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: null }));
      }
    };

    const validateForm = () => {
      const newErrors = {};

      if (!basicInfo.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(basicInfo.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!basicInfo.userName?.trim()) {
        newErrors.userName = 'Name is required';
      }

      if (!hasAllFacilitiesAccess && selectedFacilities.length === 0) {
        newErrors.facilities = 'At least one facility must be selected';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    useImperativeHandle(
      ref,
      () => ({
        validateForm,
        isFormValid:
          !errors.email &&
          !errors.userName &&
          basicInfo.email &&
          basicInfo.userName,
      }),
      [
        errors,
        basicInfo.email,
        basicInfo.userName,
        selectedFacilities,
        hasAllFacilitiesAccess,
      ],
    );

    return (
      <Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            {t('facilityUserManagement.status.label')}:
          </Typography>
          <FacilityUserStatusChip status={user?.user_data?.status} />
        </Box>
        {user?.user_data?.status === USER_STATUS.ONBOARDING && (
          <Alert severity='info' sx={{ mb: 4 }}>
            In order to make the user Active, you need to assign at least one
            facility to the user.
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          }}>
          <TextField
            type='email'
            label={t('facilityUserManagement.modal.emailUsernameLabel')}
            value={basicInfo.email}
            onChange={(e) => handleFormChange('email', e.target.value)}
            disabled={true}
            size='small'
            fullWidth
            error={!!errors.email}
            helperText={errors.email}
            autoComplete='off'
          />

          <TextField
            type='text'
            label={t('facilityUserManagement.modal.nameLabel')}
            value={basicInfo.userName}
            onChange={(e) => handleFormChange('userName', e.target.value)}
            disabled={true}
            size='small'
            fullWidth
            error={!!errors.userName}
            helperText={errors.userName}
            autoComplete='off'
          />

          <OrganizationsDropdown
            value={selectedOrganization}
            onChange={(e) => onOrganizationChange(e.target.value)}
            label='Organization'
            size='small'
            fullWidth
            disabled={true}
          />

          <FormControl variant='outlined' size='small' fullWidth>
            <InputLabel id='language-preference'>
              {t('facilityUserManagement.modal.languagePreferenceLabel')}
            </InputLabel>
            <Select
              labelId='language-preference'
              value={basicInfo.languagePreference}
              onChange={(e) =>
                handleFormChange('languagePreference', e.target.value)
              }
              label={t('facilityUserManagement.modal.languagePreferenceLabel')}>
              {supportedLanguages.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant='outlined' size='small' fullWidth>
            <InputLabel id='mfa-type'>MFA Type</InputLabel>
            <Select
              labelId='mfa-type'
              value={basicInfo.mfa_type}
              onChange={(e) => handleFormChange('mfa_type', e.target.value)}
              label='MFA Type'>
              <MenuItem value={'NONE'}>None</MenuItem>
              <MenuItem value={'EMAIL_OTP'}>Email OTP</MenuItem>
              <MenuItem value={'SOFTWARE_TOKEN_MFA'}>Software MFA</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3, mt: 4 }} />

        <Typography variant='h6' gutterBottom>
          Permission Assignment
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControl component='fieldset'>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={permissionsType === 'role'}
                    onChange={(e) =>
                      onPermissionsTypeChange(
                        e.target.checked ? 'role' : 'permission',
                      )
                    }
                    color='primary'
                  />
                }
                label='Use Role-based Permissions'
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={permissionsType === 'permission'}
                    onChange={(e) =>
                      onPermissionsTypeChange(
                        e.target.checked ? 'permission' : 'role',
                      )
                    }
                    color='primary'
                  />
                }
                label='Custom Permissions'
              />
            </Box>
          </FormControl>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            {permissionsType === 'role'
              ? 'User will inherit permissions from the selected role'
              : 'Configure custom permissions manually for this user'}
          </Typography>
        </Box>

        {permissionsType === 'role' && (
          <Box sx={{ mb: 3 }}>
            {!selectedOrganization && (
              <Alert severity='warning' sx={{ mb: 2 }}>
                <Typography variant='body2'>
                  Please select an organization above to see available roles.
                </Typography>
              </Alert>
            )}
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              }}>
              <RolesDropdown
                value={selectedRoleId || ''}
                onRoleChange={onRoleChange}
                label='Role'
                fullWidth
                showDescription={true}
                organizationId={selectedOrganization}
                showPreview={true}
                setRoleBasedFacilitiesAccess={setRoleBasedFacilitiesAccess}
              />
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 3, mt: 4 }} />

        <Typography variant='h6' gutterBottom sx={{ mt: 3 }}>
          Facility Access
        </Typography>

        <FacilityAccessComponent
          hasAllFacilitiesAccess={hasAllFacilitiesAccess}
          onHasAllFacilitiesAccessChange={onHasAllFacilitiesAccessChange}
          roleBasedFacilitiesAccess={roleBasedFacilitiesAccess}
          selectedFacilities={selectedFacilities}
          onSelectedFacilitiesChange={onSelectedFacilitiesChange}
          selectedOrganization={selectedOrganization}
          errors={errors}
        />

        <Divider sx={{ my: 3, mt: 4 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant='outlined'
            color='warning'
            onClick={onResendActivation}>
            Resend Activation Email
          </Button>
        </Box>
      </Box>
    );
  },
);

UserBasicInfoSection.displayName = 'UserBasicInfoSection';

export default UserBasicInfoSection;
